import type { Page } from "@sveltejs/kit";
import type { Locale, Price } from "@agapi/mlc-core";
import type { AvailableCountryCode, AvailableCountryCodes } from "../types/locale";

import { PUBLIC_DEFAULT_MARKET, PUBLIC_AVAILABLE_MARKETS } from '$env/static/public';

import { percentOff } from '$lib/parachute/messages';
import { locales, baseLocale } from '$lib/parachute/runtime';
import { localizeUrl } from '$lib/parachute/routes';

interface Currencies {
  [key: string]: string;
}

// Create instances once and reuse (for performance)
const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });
const DEFAULT_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
const REPLACE_LOCALE_REGEX = new RegExp(`^/(${locales.join('|')})(?=/|$)`);
const AVAILABLE_COUNTRIES: AvailableCountryCodes = PUBLIC_AVAILABLE_MARKETS.split(',');
const EU_WITH_SCANDINAVIA_OMITTED_COUNTRIES = ['AT', 'BE', 'BG', 'CZ', 'DE', 'EE', 'ES', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SI', 'SK'];

const CURRENCIES: Currencies = {
  SE: 'SEK',
  DK: 'DKK',
  NO: 'NOK',
  GB: 'GBP',
  DEFAULT: 'EUR'
};

export const getDefaultLanguage = () => {
  return baseLocale;
}

export const getDefaultMarket = () => {
  return PUBLIC_DEFAULT_MARKET;
}

export const getAvailableMarkets = (): AvailableCountryCodes => {
  return PUBLIC_AVAILABLE_MARKETS.split(',');
}

export const getIsMultiMarket = () => {
  return getAvailableMarkets().length > 1;
}

/**
 * Gets the default locale for a country by combining its primary language code with the country code
 * @param countryCode - The ISO 3166-1 country code (e.g., 'SE' for Sweden)
 * @returns The locale string in the format 'language_COUNTRY' (e.g., 'sv_SE' for Swedish/Sweden)
 */
const getCountryDefaultLocale = (countryCode: AvailableCountryCode) => {
  REGION_NAMES.of(countryCode); // Validate country code
  const locale = new Intl.Locale('und', { region: countryCode });
  const language = locale.maximize().language;
  return `${language}-${countryCode}`;
}

/**
 * Gets the country code from a language code
 * @param languageCode - The language code (e.g., 'sv' for Swedish)
 * @returns The ISO 3166-1 country code (e.g., 'SE' for Sweden)
 */
const getCountryCodeFromLanguage = (languageCode: string): AvailableCountryCode => {
  // Create a locale with the language and maximize it to get the likely region
  const locale = new Intl.Locale(languageCode);
  const maximizedLocale = locale.maximize();
  const countryCode = maximizedLocale.region?.toUpperCase();

  if (!countryCode) {
    throw new Error(`Could not determine country code for language: ${languageCode}`);
  }

  if (!AVAILABLE_COUNTRIES.includes(countryCode as AvailableCountryCode)) {
    throw new Error(`Country code '${countryCode}' is not available in the current markets`);
  }

  return countryCode as AvailableCountryCode;
}

export const getLocaleFormattedAmount = (price: number, locale: Locale) => {
  return transformToCurrency(price / 100, locale);
}

export const transformToCurrency = (value: number, locale: Locale) => {
  const { countryDefaultLocale, currency } = locale;
  const maximumFractionDigits = Math.floor((value - Math.floor(value)) * 100) > 0 ? 2 : 0;
  return new Intl.NumberFormat(countryDefaultLocale, { style: 'currency', currency, maximumFractionDigits }).format(value);
}

export const getLocaleFormattedPrice = (price: Price, locale: Locale) => {
  const discount = (price.discount ?? 0) / 100;
  const amount = price.amount / 100;

  return {
    totalPrice: transformToCurrency(amount - discount, locale),
    originalPrice: discount > 0 ? transformToCurrency(amount, locale) : undefined,
    discountLabel: discount > 0 ? percentOff({ percent: Math.round((discount / amount) * 100).toString() }) : undefined
  }
}

export const formatDate = (dateString: string, locale: Locale): string => {
  return new Date(dateString).toLocaleDateString(locale.language, DEFAULT_DATE_FORMAT_OPTIONS);
}

const getDefaultCurrency = (market: string) => {
  const currency = CURRENCIES[market.toUpperCase()] || CURRENCIES['DEFAULT'];
  return currency;
};

export const isEUWithScandinaviaOmitted = (market: string) => {
  return EU_WITH_SCANDINAVIA_OMITTED_COUNTRIES.includes(market);
}

export const getLocaleFromParams = (params?: Partial<{ country: string | null, language: string | null }>): Locale => {
  const country = params?.country || PUBLIC_DEFAULT_MARKET;
  const language = params?.language || baseLocale;

  const currency = getDefaultCurrency(country);
  const locale = `${language}-${country}`;
  const countryDefaultLocale = getCountryDefaultLocale(country as AvailableCountryCode);

  return {
    country,
    language,
    currency,
    locale,
    countryDefaultLocale,
    isMultiMarket: getAvailableMarkets().length > 1,
    defaultCountry: PUBLIC_DEFAULT_MARKET,
    defaultLanguage: baseLocale
  };
}

export const getLocaleFromUrl = (url: URL): Locale => {
  const country = getCountry(url);
  const language = getLanguage(url);
  return getLocaleFromParams({ country, language });
}

export const getLanguage = (url: URL): string => {
  const segment = url.pathname.split('/')?.filter((s) => s).shift()?.toLowerCase();
  return segment && locales.includes(segment) ? segment : baseLocale;
}

export const getCountry = (url: URL): string => {
  const country = url.searchParams.get('country')?.toUpperCase()?.replace(/[^a-zA-Z]/g, '');
  if (country && AVAILABLE_COUNTRIES.includes(country as AvailableCountryCode)) {
    return country as AvailableCountryCode;
  }
  return PUBLIC_DEFAULT_MARKET;
}

const clearBlacklistedSearchParams = (newUrl: URL, pageUrl: URL) => {
  const searchParams = new URLSearchParams();
  if (newUrl.pathname.endsWith('/search') && newUrl.searchParams.has('q')) {
    searchParams.set('q', newUrl.searchParams.get('q') as string);
  }

  if (pageUrl.searchParams.has('country')) {
    searchParams.set('country', pageUrl.searchParams.get('country') as string);
  }

  return searchParams;
}

export const rewriteUrl = <T extends (string | undefined)>(page: Page, path: T): T => {
  if (!path) {
    return path;
  }

  if (typeof path !== 'string') {
    throw new Error('"url" is not a string');
  }

  const newUrl = new URL(path, page.url.origin);
  newUrl.search = clearBlacklistedSearchParams(newUrl, page.url).toString();
  newUrl.href = localizeUrl(newUrl.href);

  return newUrl.toString() as T;
}

export const removeLocaleFromPath = (path: string) => {
  return path.replace(REPLACE_LOCALE_REGEX, '');
}

/**
 * Get the default country for a given language
 * Used as fallback when clientCountry is not available
 */
export const getDefaultCountryForLanguage = (language: string): AvailableCountryCode => {
  const defaultMarketPerLanguage: Record<string, AvailableCountryCode> = {
    'sv': 'SE',  // Swedish -> Sweden
    'en': 'GB',  // English -> Great Britain
    'da': 'DK',  // Danish -> Denmark
    'fi': 'FI',  // Finnish -> Finland
    'nb': 'NO',  // Norwegian -> Norway
    'es': 'ES',  // Spanish -> Spain
  };

  const defaultCountry = defaultMarketPerLanguage[language.toLowerCase()];

  // Ensure the country is in available markets, otherwise fall back to default market
  const availableMarkets = getAvailableMarkets();
  if (defaultCountry && availableMarkets.includes(defaultCountry)) {
    return defaultCountry;
  }

  return PUBLIC_DEFAULT_MARKET as AvailableCountryCode;
};

export const getHomeUrl = (locale: Locale): string => {
  return locale.language !== locale.defaultLanguage ? `/${locale.language}` : '/';
};
